import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pb-24">
      <div className="mx-auto max-w-3xl px-5 pt-20 text-center lg:px-8">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">Contact</p>
        <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tighter md:text-5xl">
          Let's talk clean bins
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">
          Questions, custom jobs, or commercial requests? Drop us a line.
        </p>
      </div>

      <div className="mx-auto mt-14 grid max-w-5xl gap-8 px-5 lg:grid-cols-5 lg:px-8">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Mail className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold">Email us</h3>
            <a href="mailto:ogmoss@renewmybins.com" target="_top"
              onClick={() => { navigator.clipboard.writeText("ogmoss@renewmybins.com").then(() => toast.success("Email copied: ogmoss@renewmybins.com")).catch((err) => { if (process.env.NODE_ENV === "development") console.error("Clipboard copy failed:", err); }); }}
              className="mt-1 block text-primary hover:underline">
              ogmoss@renewmybins.com
            </a>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <MapPin className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold">Service area</h3>
            <p className="mt-1 text-muted-foreground">
              Charles, Calvert, Southern PG County & DC.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-8 lg:col-span-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-semibold">Name</label>
              <input data-testid="contact-name" required value={form.name} onChange={set("name")}
                className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm font-semibold">Phone</label>
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")}
                className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold">Email</label>
            <input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")}
              className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold">Message</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")}
              className="mt-1 w-full rounded-xl border border-border bg-muted/50 px-4 py-3 focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={sending}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 font-bold text-primary-foreground transition-all hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-50">
            <Send className="h-5 w-5" /> {sending ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
